import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { PartySelect } from "@/components/catalog/PartySelect";
import { ProductSelect, type LedgerProductOption } from "@/components/catalog/ProductSelect";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { listPartyOptions, syncPartiesFromApi, type PartyOption } from "@/lib/parties/partiesStore";
import { renderBalance, renderSignedAmount } from "@/lib/accounts/ledgerReportDisplay";
import {
  enrichPartyLedgerLines,
  resolvePartyRole,
  type PartyLedgerCategory,
  type PartyRoleHint,
} from "@/lib/parties/partyLedgerDisplay";
import {
  downloadPartyLedgerExcel,
  downloadPartyLedgerPdf,
  fetchPartyLedger,
  fetchPartyLedgerProductOptions,
  type PartyLedger,
  type PartyLedgerTypeFilter,
} from "@/lib/parties/partyLedgerStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import {
  ArrowDownCircle,
  ArrowLeftRight,
  BookOpen,
  Car,
  FileDown,
  FileSpreadsheet,
  FileText,
  Loader2,
  RefreshCw,
  RotateCcw,
  Search,
  Wallet,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";

function formatDate(value: string | null): string {
  if (!value) return "";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

const TYPE_OPTIONS: { value: PartyLedgerTypeFilter; label: string }[] = [
  { value: "all", label: "All parties" },
  { value: "customer", label: "Customer" },
  { value: "supplier", label: "Supplier" },
  { value: "investor", label: "Investor" },
];

function partyMatchesType(party: PartyOption, type: PartyLedgerTypeFilter): boolean {
  if (type === "all") return true;
  const t = (party.type ?? "").toLowerCase();
  return t === type || t === "all" || t === "both";
}

function categoryIcon(category: PartyLedgerCategory): ReactNode {
  const className = "h-4 w-4";
  switch (category) {
    case "car_sold":
      return <Car className={className} />;
    case "payment_received":
      return <Wallet className={className} />;
    case "refund_paid":
      return <ArrowDownCircle className={className} />;
    case "car_exchange":
      return <ArrowLeftRight className={className} />;
    case "exchange_adjustment":
      return <RefreshCw className={className} />;
    case "opening_balance":
      return <BookOpen className={className} />;
    default:
      return <FileText className={className} />;
  }
}

function remainingBalanceLabel(
  amount: number,
  side: "Dr" | "Cr",
  role: PartyRoleHint,
): { secondary: string | null; settled: boolean } {
  const abs = Math.abs(amount);
  if (abs < 0.005) {
    return { secondary: "Settled", settled: true };
  }
  if (role === "supplier") {
    return {
      secondary: side === "Cr" ? "Amount Payable" : "Supplier Advance",
      settled: false,
    };
  }
  if (role === "investor") {
    return {
      secondary: side === "Dr" ? "Amount Receivable" : "Amount Payable",
      settled: false,
    };
  }
  return {
    secondary: side === "Dr" ? "Amount Due" : "Customer Advance",
    settled: false,
  };
}

const PartyLedgerPage = () => {
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView =
    can(user, "customer_can_view_any") ||
    can(user, "supplier_can_view_any") ||
    can(user, "investor_can_view_any") ||
    can(user, "chart_of_account_can_view_any");

  const [isForbidden, setIsForbidden] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [hasApplied, setHasApplied] = useState(false);
  const [ledger, setLedger] = useState<PartyLedger | null>(null);
  const [parties, setParties] = useState<PartyOption[]>(() => listPartyOptions());

  const initialType = (() => {
    const raw = (searchParams.get("type") ?? "").toLowerCase();
    return raw === "customer" || raw === "supplier" || raw === "investor" ? raw : "all";
  })();
  const initialPartyId = searchParams.get("party_id")?.trim() || "";

  const [typeFilter, setTypeFilter] = useState<PartyLedgerTypeFilter>(initialType);
  const [partyId, setPartyId] = useState(initialPartyId);
  const [productId, setProductId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [productOptions, setProductOptions] = useState<LedgerProductOption[]>([]);
  const autoAppliedKeyRef = useRef<string | null>(null);
  const [currencyMode, setCurrencyMode] = useState<"base" | "party">("base");

  useEffect(() => {
    setCurrencyMode("base");
  }, [partyId]);

  const currencySymbol = user?.company_branding?.currency?.symbol?.trim() || "";

  const activeFilters = useMemo(
    () => ({
      type: typeFilter,
      partyId: partyId ? Number(partyId) : null,
      productId: productId ? Number(productId) : null,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
    }),
    [typeFilter, partyId, productId, dateFrom, dateTo],
  );

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void syncPartiesFromApi()
      .then(() => {
        if (!cancelled) setParties(listPartyOptions());
      })
      .catch(() => {
        /* keep cached */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchPartyLedgerProductOptions({
      type: typeFilter,
      partyId: partyId ? Number(partyId) : null,
    })
      .then((rows) => {
        if (cancelled) return;
        setProductOptions(rows);
        if (productId && !rows.some((r) => String(r.id) === String(productId))) {
          setProductId("");
        }
      })
      .catch((error) => {
        console.error("[PartyLedger] Product options failed", error);
        if (!cancelled) setProductOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [typeFilter, partyId]);

  const partyOptions = useMemo(
    () => parties.filter((p) => partyMatchesType(p, typeFilter)),
    [parties, typeFilter],
  );

  const applyFilters = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setIsForbidden(false);
    try {
      const data = await fetchPartyLedger(activeFilters);
      setLedger(data);
      setHasApplied(true);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        setIsForbidden(true);
        return;
      }
      console.error("[PartyLedger] Load failed", error);
      toast.error("Could not load party ledger.");
    } finally {
      setLoading(false);
    }
  }, [canView, activeFilters]);

  useEffect(() => {
    if (!canView || !initialPartyId) return;
    const key = `${initialType}|${initialPartyId}`;
    if (autoAppliedKeyRef.current === key) return;
    autoAppliedKeyRef.current = key;
    void applyFilters();
  }, [canView, initialType, initialPartyId, applyFilters]);

  const exportReport = useCallback(
    async (format: "pdf" | "excel") => {
      if (!hasApplied || exporting) return;
      setExporting(format);
      try {
        if (format === "pdf") {
          await downloadPartyLedgerPdf({ ...activeFilters, currencyMode });
          toast.success("PDF downloaded.");
        } else {
          await downloadPartyLedgerExcel({ ...activeFilters, currencyMode });
          toast.success("Excel downloaded.");
        }
      } catch (error) {
        console.error(`[PartyLedger] ${format} export failed`, error);
        toast.error(format === "pdf" ? "Could not download PDF." : "Could not download Excel.");
      } finally {
        setExporting(null);
      }
    },
    [hasApplied, exporting, activeFilters, currencyMode],
  );

  const resetFilters = () => {
    setTypeFilter("all");
    setPartyId("");
    setProductId("");
    setDateFrom("");
    setDateTo("");
    setLedger(null);
    setHasApplied(false);
    setCurrencyMode("base");
  };

  const titleType = TYPE_OPTIONS.find((o) => o.value === typeFilter)?.label ?? "All parties";
  const selectedParty = partyId
    ? partyOptions.find((p) => String(p.id) === String(partyId)) ??
      ledger?.parties.find((p) => String(p.id) === String(partyId))
    : null;
  const selectedPartyName = selectedParty?.name ?? null;
  const selectedProductName = productId
    ? productOptions.find((p) => String(p.id) === String(productId))?.name
    : null;

  const showCurrencySwitch =
    ledger?.partyCurrency != null &&
    ledger.partyCurrency.code.toLowerCase() !== "pkr" &&
    ledger.partyCurrency.code.toLowerCase() !== currencySymbol.toLowerCase();

  const activeSymbol =
    currencyMode === "party" && ledger?.partyCurrency
      ? ledger.partyCurrency.symbol
      : currencySymbol;

  const displayLines = useMemo(() => {
    if (!ledger) return [];
    return ledger.lines.map((line) => {
      if (currencyMode !== "party") return line;
      return {
        ...line,
        debit: line.partyDebit,
        credit: line.partyCredit,
        balance: line.partyBalance,
        balanceSide: line.partyBalanceSide,
      };
    });
  }, [ledger, currencyMode]);

  const activeOpeningBalance =
    currencyMode === "party" && ledger
      ? ledger.totals.partyOpeningBalance
      : (ledger?.totals.openingBalance ?? 0);

  const activeOpeningBalanceSide =
    currencyMode === "party" && ledger
      ? ledger.totals.partyOpeningBalanceSide
      : (ledger?.totals.openingBalanceSide ?? "Dr");

  const activeTotalBalance =
    currencyMode === "party" && ledger
      ? ledger.totals.partyBalance
      : (ledger?.totals.balance ?? 0);

  const activeTotalBalanceSide =
    currencyMode === "party" && ledger
      ? ledger.totals.partyBalanceSide
      : (ledger?.totals.balanceSide ?? "Dr");

  const role = resolvePartyRole(typeFilter, selectedParty?.type ?? ledger?.parties[0]?.type);

  const classifiedRows = useMemo(
    () => enrichPartyLedgerLines(displayLines, role, activeSymbol || "Rs."),
    [displayLines, role, activeSymbol],
  );

  const hasOpening = ledger != null && Math.abs(activeOpeningBalance) >= 0.005;
  const hasRows = classifiedRows.length > 0 || hasOpening;
  const moneySymbol = activeSymbol || "Rs.";
  const closingLabel = remainingBalanceLabel(activeTotalBalance, activeTotalBalanceSide, role);
  const amountColLabel = `Amount (${moneySymbol})`;
  const balanceColLabel = `Remaining balance (${moneySymbol})`;

  if (!canView || isForbidden) {
    return (
      <Layout>
        <PermissionDenied />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <h1 className="font-display text-xl font-bold tracking-tight text-foreground">Party Ledger</h1>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Track sales, receipts, refunds and car exchanges for each party.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-border/55 bg-card/80 p-4 shadow-sm ring-1 ring-border/25">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Party type</Label>
              <Select
                value={typeFilter}
                onValueChange={(v) => {
                  setTypeFilter(v as PartyLedgerTypeFilter);
                  setPartyId("");
                }}
              >
                <SelectTrigger className="glass-input h-9 text-xs">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Party</Label>
              <PartySelect
                value={partyId}
                options={partyOptions}
                onChange={setPartyId}
                placeholder={`All ${typeFilter === "all" ? "parties" : `${typeFilter}s`}`}
                clearable
                clearLabel={`All ${typeFilter === "all" ? "parties" : `${typeFilter}s`}`}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Product</Label>
              <ProductSelect
                value={productId}
                options={productOptions}
                onChange={setProductId}
                placeholder="All products"
                clearable
                clearLabel="All products"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">From date</Label>
              <DatePartsPicker
                value={dateFrom}
                onChange={setDateFrom}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                maxDate={dateTo ? new Date(`${dateTo}T00:00:00`) : undefined}
                placeholder="Select from date"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">To date</Label>
              <DatePartsPicker
                value={dateTo}
                onChange={setDateTo}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                minDate={dateFrom ? new Date(`${dateFrom}T00:00:00`) : undefined}
                placeholder="Select to date"
                align="end"
              />
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              {hasApplied ? (
                <>
                  Showing: <span className="font-semibold text-foreground">{titleType}</span>
                  {selectedPartyName ? (
                    <>
                      {" · "}
                      <span className="font-semibold text-foreground">{selectedPartyName}</span>
                    </>
                  ) : null}
                  {selectedProductName ? (
                    <>
                      {" · "}
                      <span className="font-semibold text-foreground">{selectedProductName}</span>
                    </>
                  ) : null}
                </>
              ) : (
                "Choose filters, then click Apply filter."
              )}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={!hasApplied || loading || exporting != null}
                onClick={() => void exportReport("pdf")}
              >
                {exporting === "pdf" ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FileDown className="mr-1.5 h-3.5 w-3.5" />
                )}
                PDF
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={!hasApplied || loading || exporting != null}
                onClick={() => void exportReport("excel")}
              >
                {exporting === "excel" ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" />
                )}
                Excel
              </Button>
              <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={resetFilters}>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Reset
              </Button>
              <Button
                type="button"
                variant="premium"
                size="sm"
                className="h-8 text-xs"
                onClick={() => void applyFilters()}
                disabled={loading}
              >
                {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Search className="mr-1.5 h-3.5 w-3.5" />}
                Apply filter
              </Button>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-border/55 bg-card/80 shadow-sm ring-1 ring-border/25">
          {loading ? (
            <div className="flex h-48 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : !hasApplied ? (
            <div className="px-6 py-14 text-center">
              <BookOpen className="mx-auto h-8 w-8 text-muted-foreground/50" />
              <p className="mt-3 text-sm font-medium text-foreground">No ledger loaded yet</p>
              <p className="mt-1 text-sm text-muted-foreground">Apply a filter to view party transactions.</p>
            </div>
          ) : !ledger || !hasRows ? (
            <div className="px-6 py-14 text-center">
              <FileText className="mx-auto h-8 w-8 text-muted-foreground/50" />
              <p className="mt-3 text-sm font-medium text-foreground">No transactions found</p>
              <p className="mt-1 text-sm text-muted-foreground">No posted ledger entries for this filter.</p>
            </div>
          ) : (
            <div>
              {selectedPartyName ? (
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/55 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground">{selectedPartyName}</p>
                    <p className="text-xs text-muted-foreground">
                      {titleType}
                      {selectedProductName ? ` · ${selectedProductName}` : " · All products"}
                    </p>
                  </div>
                  <span
                    className={cn(
                      "rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
                      closingLabel.settled
                        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {closingLabel.settled ? "Settled" : closingLabel.secondary}
                  </span>
                </div>
              ) : null}

              {showCurrencySwitch ? (
                <div className="flex items-center justify-between border-b border-border/55 bg-muted/20 px-4 py-2.5">
                  <span className="text-xs font-semibold text-muted-foreground">Ledger currency</span>
                  <div className="flex items-center gap-1.5 rounded-lg border border-border/60 bg-background p-0.5">
                    <button
                      type="button"
                      className={cn(
                        "rounded-md px-3 py-1 text-xs font-medium transition-all",
                        currencyMode === "base"
                          ? "bg-primary text-primary-foreground shadow-xs"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                      onClick={() => setCurrencyMode("base")}
                    >
                      Base ({currencySymbol || "PKR"})
                    </button>
                    <button
                      type="button"
                      className={cn(
                        "rounded-md px-3 py-1 text-xs font-medium transition-all",
                        currencyMode === "party"
                          ? "bg-primary text-primary-foreground shadow-xs"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                      onClick={() => setCurrencyMode("party")}
                    >
                      Party ({ledger.partyCurrency?.code})
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="border-b border-border/55 px-4 py-2.5">
                <h2 className="text-sm font-semibold text-foreground">Transaction history</h2>
              </div>

              {/* Desktop / tablet table-like list */}
              <div className="hidden md:block">
                <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,0.7fr)_minmax(0,0.7fr)] gap-3 border-b border-border/40 bg-muted/20 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <div>Transaction</div>
                  <div className="text-right">{amountColLabel}</div>
                  <div className="text-right">{balanceColLabel}</div>
                </div>

                <div className="divide-y divide-border/40">
                  <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,0.7fr)_minmax(0,0.7fr)] gap-3 bg-muted/15 px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                        <BookOpen className="h-3.5 w-3.5" />
                      </div>
                      <p className="text-sm font-semibold text-foreground">Opening balance</p>
                    </div>
                    <div className="text-right text-sm tabular-nums text-muted-foreground">—</div>
                    <div className="text-right text-sm">
                      {renderBalance(activeOpeningBalance, activeOpeningBalanceSide)}
                    </div>
                  </div>

                  {classifiedRows.map((row) => {
                    const bal = remainingBalanceLabel(row.balance, row.balanceSide, role);
                    return (
                      <div
                        key={row.line.id}
                        className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,0.7fr)_minmax(0,0.7fr)] gap-3 px-4 py-2.5 transition-colors hover:bg-muted/10"
                      >
                        <div className="flex items-start gap-2.5 min-w-0">
                          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/60 bg-background text-muted-foreground">
                            {categoryIcon(row.category)}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold leading-snug text-foreground">{row.categoryLabel}</p>
                            {row.purpose ? (
                              <p className="mt-0.5 text-xs leading-snug text-foreground/85">
                                {row.purpose}
                              </p>
                            ) : null}
                            <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0 text-[11px] text-muted-foreground">
                              {row.line.date ? <span>{formatDate(row.line.date)}</span> : null}
                              {row.line.date && row.line.voucher ? <span aria-hidden>·</span> : null}
                              {row.line.voucher ? (
                                <span className="font-mono">{row.line.voucher}</span>
                              ) : null}
                              {(row.line.date || row.line.voucher) && row.saleId ? (
                                <span aria-hidden>·</span>
                              ) : null}
                              {row.saleId ? (
                                <Link
                                  to={`/product-sales/${row.saleId}`}
                                  className="text-primary hover:underline"
                                >
                                  Sale #{row.saleId}
                                </Link>
                              ) : null}
                              {row.line.productId &&
                              row.line.productName &&
                              row.purpose?.toLowerCase() !== row.line.productName.toLowerCase() ? (
                                <>
                                  <span aria-hidden>·</span>
                                  <Link
                                    to={`/products/${row.line.productId}`}
                                    className="text-primary hover:underline"
                                  >
                                    {row.line.productName}
                                  </Link>
                                </>
                              ) : row.line.productId && !row.purpose ? (
                                <>
                                  {(row.line.date || row.line.voucher || row.saleId) && (
                                    <span aria-hidden>·</span>
                                  )}
                                  <Link
                                    to={`/products/${row.line.productId}`}
                                    className="text-primary hover:underline"
                                  >
                                    {row.line.productName}
                                  </Link>
                                </>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="text-right self-center text-sm">
                          {renderSignedAmount(row.signedAmount)}
                        </div>
                        <div className="text-right self-center">
                          <div className="text-sm">
                            {renderBalance(row.balance, row.balanceSide)}
                          </div>
                          {bal.secondary ? (
                            <p
                              className={cn(
                                "text-[11px] leading-tight",
                                bal.settled
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : "text-muted-foreground",
                              )}
                            >
                              {bal.secondary}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}

                  <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,0.7fr)_minmax(0,0.7fr)] gap-3 bg-muted/20 px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                        <ArrowDownCircle className="h-3.5 w-3.5" />
                      </div>
                      <p className="text-sm font-semibold text-foreground">Closing balance</p>
                    </div>
                    <div />
                    <div className="text-right">
                      <div className="text-sm">
                        {renderBalance(activeTotalBalance, activeTotalBalanceSide)}
                      </div>
                      {closingLabel.secondary ? (
                        <p
                          className={cn(
                            "text-[11px] leading-tight",
                            closingLabel.settled
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-muted-foreground",
                          )}
                        >
                          {closingLabel.secondary}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile stacked cards */}
              <div className="divide-y divide-border/40 md:hidden">
                <div className="border-b border-border/40 bg-muted/20 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {amountColLabel} · {balanceColLabel}
                </div>
                <div className="px-4 py-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                        <BookOpen className="h-3.5 w-3.5" />
                      </div>
                      <p className="text-sm font-semibold">Opening balance</p>
                    </div>
                    <div className="text-sm">
                      {renderBalance(activeOpeningBalance, activeOpeningBalanceSide)}
                    </div>
                  </div>
                </div>
                {classifiedRows.map((row) => {
                  const bal = remainingBalanceLabel(row.balance, row.balanceSide, role);
                  return (
                    <div key={row.line.id} className="px-4 py-2.5">
                      <div className="flex items-start gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/60 bg-background text-muted-foreground">
                          {categoryIcon(row.category)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-foreground">{row.categoryLabel}</p>
                          {row.purpose ? (
                            <p className="mt-0.5 text-xs leading-snug text-foreground/85">{row.purpose}</p>
                          ) : null}
                          <p className="mt-0.5 text-[11px] text-muted-foreground">
                            {[
                              row.line.date ? formatDate(row.line.date) : null,
                              row.line.voucher,
                              row.saleId ? `Sale #${row.saleId}` : null,
                              row.line.productName &&
                              row.purpose?.toLowerCase() !== row.line.productName.toLowerCase()
                                ? row.line.productName
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                          <div className="mt-1 flex items-end justify-between gap-3">
                            <div className="text-sm">{renderSignedAmount(row.signedAmount)}</div>
                            <div className="text-right">
                              <div className="text-sm">
                                {renderBalance(row.balance, row.balanceSide)}
                              </div>
                              {bal.secondary ? (
                                <p className="text-[11px] text-muted-foreground">{bal.secondary}</p>
                              ) : null}
                            </div>
                          </div>
                          {row.line.productId || row.saleId ? (
                            <div className="mt-1 flex flex-wrap gap-x-2 text-[11px]">
                              {row.line.productId && row.line.productName ? (
                                <Link to={`/products/${row.line.productId}`} className="text-primary hover:underline">
                                  Product
                                </Link>
                              ) : null}
                              {row.saleId ? (
                                <Link to={`/product-sales/${row.saleId}`} className="text-primary hover:underline">
                                  Sale #{row.saleId}
                                </Link>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className="bg-muted/20 px-4 py-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold">Closing balance</p>
                    <div className="text-right">
                      <div className="text-sm">
                        {renderBalance(activeTotalBalance, activeTotalBalanceSide)}
                      </div>
                      {closingLabel.secondary ? (
                        <p className="text-[11px] text-muted-foreground">{closingLabel.secondary}</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PartyLedgerPage;
